import React, { useState, useEffect, useCallback, useRef } from 'react';
import UniversalIconBtn from './headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { debounce } from '../util/debounce';
import ArrowDown from '../../asset/icon/below.png';
import ArrowUp from '../../asset/icon/up.png';
import { blink } from './Animation';
import { useEditMode } from '../hooks/useEditMode';

const DEBOUNCE_DELAY = 100;

export type ScrollDownArrowPlacement = 'center' | 'end';

export interface ScrollDownArrowProps {
  placement?: ScrollDownArrowPlacement;
  scrollContainerSelector?: string;
  /** Landing: 더 크게 내리고, 맨 아래에서는 위로 되돌린다. */
  bidirectional?: boolean;
}

const ScrollDownArrow: React.FC<ScrollDownArrowProps> = ({
  placement = 'center',
  scrollContainerSelector,
  bidirectional = false,
}) => {
  const { isEditMode } = useEditMode();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const overflowY = window.getComputedStyle(container).overflowY;
    const canUserScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    const hasScroll = canUserScroll && container.scrollHeight > container.clientHeight;

    if (!hasScroll) {
      setIsVisible(false);
      setIsClosing(false);
      setIsAtEnd(false);
      return;
    }

    const isAtEndNow = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;
    if (bidirectional) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsAtEnd(isAtEndNow);
      setIsVisible(true);
      setIsClosing(false);
      return;
    }

    const isAtTop = container.scrollTop <= 30;

    if (isAtTop) {
      // 위로 올라오면 숨김 타이머 제거 + 다시 보이게
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsVisible(true);
      setIsClosing(false);
    } else {
      setIsClosing(true);

      // 기존 타이머 있으면 정리
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        hideTimeoutRef.current = null;
      }, 500);
    }
  }, [bidirectional]);

  const debouncedHandleScroll = debounce(handleScroll, DEBOUNCE_DELAY);

  useEffect(() => {
    const findScrollableContainer = (): HTMLElement | null => {
      const target = scrollContainerSelector
        ? document.querySelector(scrollContainerSelector)
        : document.querySelector('main');
      return target instanceof HTMLElement ? target : null;
    };

    let retryCount = 0;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let container: HTMLElement | null = null;
    let mutationObserver: MutationObserver | null = null;

    // 스크롤 이벤트 리스너 (Event는 무시, ref만 사용)
    const onScroll: EventListener = () => {
      debouncedHandleScroll();
    };

    const setup = () => {
      container = findScrollableContainer();
  
      if (!container && retryCount < 3) {
        retryCount++;
        retryTimeoutId = setTimeout(setup, 500);
        return;
      }
  
      if (!container) {
        console.error('Scrollable container not found after retries');
        return;
      }
  
      containerRef.current = container;
  
      // 🔹 최초 한 번 상태 반영
      handleScroll();
  
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          handleScroll();
        });
  
        mutationObserver.observe(container, {
          childList: true,
          subtree: true,
        });
      }
  
      container.addEventListener('scroll', onScroll, { passive: true });
    };

    setup();

    return () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
  
      if (container) {
        container.removeEventListener('scroll', onScroll);
      }
  
      if (mutationObserver) {
        mutationObserver.disconnect();
      }

      debouncedHandleScroll.cancel();
  
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [handleScroll, debouncedHandleScroll, scrollContainerSelector]);

  const handleClick = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (bidirectional) {
      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      const atEnd = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;
      const step = Math.floor(container.scrollHeight / 4);
      container.scrollTo({
        top: atEnd ? 0 : Math.min(maxScroll, container.scrollTop + step),
        behavior: 'smooth',
      });
      return;
    }

    const scrollDistance = Math.floor(container.scrollHeight / 10);
    container.scrollTo({
      top: scrollDistance,
      behavior: 'smooth',
    });
  }, [bidirectional]);


  if (!isVisible) return null;
  if (isEditMode) return null;

  return (
    <Arrow $isVisible={!isClosing} $placement={placement}>
      <UniversalIconBtn
        size="standard"
        border={false}
        isTransparent
        backgroundImg={bidirectional && isAtEnd ? ArrowUp : ArrowDown}
        onClick={handleClick}
      />
    </Arrow>
  );
};

const Arrow = styled.div<{ $isVisible: boolean; $placement: ScrollDownArrowPlacement }>`
  position: fixed;
  bottom: ${({ $placement }) =>
    $placement === 'end' ? 'calc(28px + env(safe-area-inset-bottom, 0px))' : '1vh'};
  ${({ $placement }) =>
    $placement === 'end'
      ? `
    right: 12px;
    left: auto;
  `
      : `
    left: 50%;
    transform: translateX(-50%);
  `}
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  ${blink}

  z-index: 3;
`;

export default ScrollDownArrow;
